import { useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { meetingsApi } from "@/api/endpoints/meetings";
import { useAuthSafe } from "@/features/auth/contexts/AuthContext";
import { isFreshlyProcessing, type MeetingRecord } from "../types";
import { readCachedMeetings, writeCachedMeetings } from "../lib/meetings-cache";

export const MEETING_KEYS = {
  all: ["meetings"] as const,
  list: () => ["meetings", "list"] as const,
  meeting: (id: string) => ["meetings", id] as const,
};

/**
 * How often to re-poll the list, by what is actually happening.
 *
 * The primary update path is the user-scoped WS push (`useUserSocket`), which
 * invalidates this query on SESSION_CREATED / SESSION_ENDED. Polling is only
 * the safety net for a dropped socket or the multi-worker WS gap.
 *
 * The previous values were `active ? 5s : inFlight ? 10s : 3s` — which made
 * the **idle** case the fastest poll of the three. A user sitting on the Meet
 * tab with nothing recording re-fetched the whole list every 3 seconds, and
 * `GET /v1/intelligence/meetings` is not a cheap endpoint: it loads every
 * session plus every intelligence row for the user with nine eager-loaded
 * child collections. That is ~20 requests a minute, each fanning out to a
 * double-digit number of queries, purely to re-confirm that nothing changed.
 * It is the main reason this page felt slow.
 */
const POLL_ACTIVE_MS = 5_000;     // something is recording — keep it live
const POLL_IN_FLIGHT_MS = 15_000; // notes are being generated
const POLL_IDLE_MS = 120_000;     // nothing happening; the socket has this

export function useMeetings(options?: { enabled?: boolean }) {
  const auth = useAuthSafe();
  const userId = auth?.authData?.userProfile?.id ?? null;

  // Read once per user, not on every render — this touches localStorage and
  // JSON.parses the whole list.
  const cached = useMemo(() => readCachedMeetings(userId), [userId]);

  const query = useQuery<MeetingRecord[]>({
    queryKey: MEETING_KEYS.list(),
    queryFn: () => meetingsApi.listMeetings(),
    enabled: options?.enabled ?? true,
    // Seed from the last good response so the tab paints immediately instead
    // of showing a spinner on every visit. `initialDataUpdatedAt` is the real
    // age of that data, so React Query treats it as stale and revalidates in
    // the background rather than trusting it as fresh.
    initialData: cached?.data,
    initialDataUpdatedAt: cached?.updatedAt,
    // 30 s: long enough that tab switches and remounts reuse the cache,
    // short enough that a returning user is never looking at a stale list for
    // meaningfully long. Paired with the drop of `refetchOnMount: "always"`,
    // which forced a request on every single mount regardless of staleness.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasActive = data?.some(
        (m) => m.status === "recording" || m.status === "paused",
      );
      // Only poll fast for in-flight meetings if at least one is *fresh* —
      // legacy/failed records can stay in "ended" forever and would otherwise
      // pin every client to a fast poll for no reason.
      const hasInFlight = data?.some(isFreshlyProcessing);
      return hasActive
        ? POLL_ACTIVE_MS
        : hasInFlight
          ? POLL_IN_FLIGHT_MS
          : POLL_IDLE_MS;
    },
  });

  // Persist every successful response. Keyed off dataUpdatedAt so a re-render
  // with unchanged data doesn't re-serialize the whole list.
  const { data, isSuccess, dataUpdatedAt } = query;
  useEffect(() => {
    if (isSuccess && data) writeCachedMeetings(userId, data);
  }, [isSuccess, dataUpdatedAt, data, userId]);

  return query;
}

export function useTrashMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => meetingsApi.trashMeeting(id),
    // Optimistic: drop the row from the list cache immediately. If the
    // DELETE fails we restore the snapshot so the row reappears with the
    // failure toast — no need to wait for the next poll to "uncrush" it.
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: MEETING_KEYS.list() });
      const previous = queryClient.getQueryData<MeetingRecord[]>(MEETING_KEYS.list());
      if (previous) {
        queryClient.setQueryData<MeetingRecord[]>(
          MEETING_KEYS.list(),
          previous.filter(
            (m) => m.id !== id && m.sessionId !== id && m.externalMeetingId !== id,
          ),
        );
      }
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(MEETING_KEYS.list(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: MEETING_KEYS.list() });
    },
  });
}

export function useStopMeeting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => meetingsApi.stopMeeting(sessionId),
    onSuccess: () => {
      // Invalidate immediately — the meeting status flips to ended/processing.
      queryClient.invalidateQueries({ queryKey: MEETING_KEYS.list() });
    },
  });
}

export function useMeeting(id: string | null) {
  return useQuery<MeetingRecord>({
    queryKey: MEETING_KEYS.meeting(id ?? ""),
    queryFn: () => meetingsApi.getMeeting(id!),
    enabled: !!id,
    staleTime: 5_000,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const m = query.state.data;
      if (!m) return false;
      if (m.status === "recording" || m.status === "paused") return 8_000;
      // Stop polling once the meeting is no longer freshly in-flight —
      // legacy records stuck at "ended" shouldn't pin every detail view to a
      // 10 s poll.
      if (isFreshlyProcessing(m)) return 10_000;
      return false;
    },
    retry: false,
  });
}
